import React from 'react';
import { Navigate, useNavigate } from 'react-router';
import { FilePen, ClipboardList, LogOut } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useClinica } from '../lib/clinica';
import LogoClinica from '../components/LogoClinica';
import { Button } from '../components/ui/button';
import { esCuentaDeTablet, guardarModoTablet, MODOS_TABLET } from '../lib/modoTablet';

/**
 * El selector de la tablet (dirección, 29/9/2026): una cuenta, dos modos.
 *
 * Al entrar con la cuenta del iPad —cualquiera de los dos roles de tablet—
 * sale esto: dos botones grandes, «Firmar consentimientos» y «Rellenar
 * LOPD». Lo elegido se recuerda en este iPad (`lib/modoTablet`) y la próxima
 * vez abre directamente ahí; en los dos quioscos hay un «Cambiar de modo»
 * discreto que vuelve aquí, sin contraseña.
 *
 * Solo para las cuentas de tablet: el equipo, a su programa; sin sesión, al
 * login. Va fuera de RequireAuth, que manda las tablets a su quiosco.
 */
const OPCIONES = [
  {
    modo: 'consentimientos',
    titulo: 'Firmar consentimientos',
    texto: 'El paciente lee y firma los consentimientos que le manda recepción.',
    icono: FilePen,
  },
  {
    modo: 'lopd',
    titulo: 'Rellenar LOPD',
    texto: 'El paciente rellena sus datos, el cuestionario de salud y firma la LOPD.',
    icono: ClipboardList,
  },
];

export default function ModoTabletPage() {
  const auth = useAuth();
  const clinicaDatos = useClinica();
  const navigate = useNavigate();
  const user = auth?.user;

  if (auth?.loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Cargando…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!esCuentaDeTablet(user)) return <Navigate to="/" replace />;

  const elegir = (modo) => {
    guardarModoTablet(modo);
    navigate(MODOS_TABLET[modo], { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F4F6FB] flex flex-col" data-testid="modo-tablet-page">
      <header className="bg-white border-b border-border px-6 py-3 flex items-center gap-3 shadow-sm">
        <LogoClinica className="h-10 w-auto" />
        <div>
          <h1 className="text-[#13231E] font-display font-bold text-lg leading-tight">Tablet</h1>
          <p className="text-xs text-muted-foreground">{clinicaDatos.clinic_name}</p>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 flex flex-col">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#13231E] text-center">
          ¿Para qué vas a usar hoy la tablet?
        </h2>
        <p className="text-sm text-muted-foreground text-center mt-2">
          Se recordará en esta tablet. Puedes cambiarlo cuando quieras con «Cambiar de modo».
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
          {OPCIONES.map(({ modo, titulo, texto, icono: Icono }) => (
            <button
              key={modo}
              type="button"
              onClick={() => elegir(modo)}
              className="group text-left bg-white rounded-2xl border-2 border-[#2E6E5E]/25 hover:border-[#2E6E5E] active:bg-[#2E6E5E]/5 shadow-sm p-8 min-h-[220px] flex flex-col justify-between transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2E6E5E]/30"
              data-testid={`modo-tablet-${modo}`}
            >
              <span className="w-16 h-16 rounded-full bg-[#2E6E5E]/10 flex items-center justify-center">
                <Icono className="w-9 h-9 text-[#2E6E5E]" aria-hidden="true" />
              </span>
              <span className="mt-6">
                <span className="block font-display text-2xl font-semibold text-[#13231E]">{titulo}</span>
                <span className="block text-sm text-muted-foreground mt-1">{texto}</span>
              </span>
            </button>
          ))}
        </div>

        <div className="mt-auto pt-10 text-center">
          <Button variant="ghost" size="sm" onClick={() => auth?.logout?.()} data-testid="modo-tablet-logout">
            <LogOut className="w-4 h-4 mr-1" /> Cerrar sesión de la tablet
          </Button>
        </div>
      </main>
    </div>
  );
}
